import { VEX_SPINNER_ENDPOINTS } from './endpoints';
import { SpinnerData } from './types';

const requests = new Map<string, Promise<SpinnerData | null>>();

/**
 * Bare `fetch`, not `helpers/axios`: that interceptor would put a customer Bearer token in a
 * cached static host's edge logs, and force an uncacheable preflight (`max-age: 0`).
 */
const load = async (stockNumber: string): Promise<SpinnerData | null> => {
  // Read at call time, not module scope: an unset host would otherwise build
  // `/undefined/spinnerdata/…` and fire it at our own origin.
  if (!process.env.SPINNER_DATA_CDN_URL) return null;

  const response = await fetch(
    `${VEX_SPINNER_ENDPOINTS.SPINNER_DATA}/${stockNumber}/spinnerData.json`
  );

  // A 404 is a definitive "this vehicle has no spin", worth remembering. Anything else that
  // fails is transient and must not be.
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`spinner data responded ${response.status}`);

  return (await response.json()) as SpinnerData;
};

/**
 * `max-age=30` makes the browser cache useless across steps. Caches the in-flight promise so
 * co-mounted surfaces share one request — which is why nothing may abort it.
 */
export const getSpinnerData = (stockNumber?: string | null): Promise<SpinnerData | null> => {
  if (!stockNumber) return Promise.resolve(null);

  const inFlight = requests.get(stockNumber);

  if (inFlight) return inFlight;

  // A failure is evicted rather than resolved-and-kept, so one blip can't pin every surface to
  // the stock-photo fallback for the rest of the session.
  const request = load(stockNumber).catch(() => {
    requests.delete(stockNumber);

    return null;
  });

  requests.set(stockNumber, request);

  return request;
};

/** Module state outlives a test file, so the second test would otherwise read the first's cache. */
export const resetCache = () => {
  requests.clear();
};
